import type { RxJsonSchema } from 'rxdb'

/**
 * Attachment Document Type (Denormalized for RxDB)
 */
export interface AttachmentDocument {
  id: string
  envId: string
  entityType: string // 'task', 'comment', etc.
  entityId: string
  fileName: string
  fileType: string // MIME type
  fileSizeBytes: number
  storageKey: string
  fileUrl: string | null
  description: string | null
  uploadStatus: number // 0=PendingUpload, 1=Uploaded, 2=Failed
  isDeleted: boolean

  // Denormalized user info
  createdByName: string | null
  createdByEmail: string | null

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for Attachment
 */
export const attachmentSchema: RxJsonSchema<AttachmentDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    entityType: {
      type: 'string',
      maxLength: 50,
    },
    entityId: {
      type: 'string',
      maxLength: 36,
    },
    fileName: {
      type: 'string',
      maxLength: 500,
    },
    fileType: {
      type: 'string',
      maxLength: 100,
    },
    fileSizeBytes: {
      type: 'number',
      minimum: 0,
      maximum: 999999999999,
      multipleOf: 1,
    },
    storageKey: {
      type: 'string',
      maxLength: 200,
    },
    fileUrl: {
      type: ['string', 'null'],
      maxLength: 500,
    },
    description: {
      type: ['string', 'null'],
      maxLength: 1000,
    },
    uploadStatus: {
      type: 'number',
      minimum: 0,
      maximum: 2,
      multipleOf: 1,
      default: 0,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Denormalized fields
    createdByName: {
      type: ['string', 'null'],
      maxLength: 100,
    },
    createdByEmail: {
      type: ['string', 'null'],
      maxLength: 255,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'entityType',
    'entityId',
    'fileName',
    'fileType',
    'fileSizeBytes',
    'storageKey',
    'uploadStatus',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'entityId',
    'updatedOn',
    ['entityId', 'isDeleted'],
    ['envId', 'isDeleted'],
    ['envId', 'uploadStatus'],
  ],
}

/**
 * Attachment schema migrations
 *
 * Version history:
 * 0 : initial schema
 */
export const attachmentMigrationStrategies = {}
